import { Link } from 'react-router-dom';
import { Users, UserCheck, Snowflake, Flame, Clock, LayoutDashboard, LogIn, UserPlus, Pencil, Trash2, FilePlus, Upload, Key, FileText, ClipboardCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import PageHeader from '../components/PageHeader';
import { useDashboard } from '../hooks/useDashboard';

export default function DashboardPage() {
  const { data, isLoading, isError } = useDashboard();

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (isError || !data) {
    return <div className="text-center py-12 text-gray-500">Error cargando dashboard</div>;
  }

  const { stats, recent_activity } = data;

  // Tenant breakdown by lifecycle status (from clients_by_status)
  const byStatus = (s: string) =>
    stats.clients_by_status.find(c => c.status === s)?.count ?? 0;
  const activeTenants = byStatus('active');
  const warmLeads = byStatus('warm_lead');
  const coldLeads = byStatus('cold_lead');

  return (
    <div className="space-y-6">
      <PageHeader
        type="dashboard"
        icon={LayoutDashboard}
        title="Dashboard"
        subtitle="Resumen de tenants y altas · Islas Canarias"
        actions={
          <Link
            to="/clients"
            className="flex items-center gap-2 px-4 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Users size={16} />
            Ver tenants
          </Link>
        }
      />

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Users size={20} />}
          label="Tenants (total)"
          value={stats.total_clients}
          color="teal"
        />
        <StatCard
          icon={<UserCheck size={20} />}
          label="Tenants activos"
          value={activeTenants}
          color="blue"
        />
        <StatCard
          icon={<Flame size={20} />}
          label="Leads cálidos"
          value={warmLeads}
          color="amber"
        />
        <StatCard
          icon={<Snowflake size={20} />}
          label="Leads fríos"
          value={coldLeads}
          color="purple"
        />
      </div>

      {/* Two columns: tenants by status + activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tenants by onboarding/lifecycle status */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Tenants por estado</h2>
            <Link to="/clients" className="text-sm text-teal-600 hover:text-teal-700 font-medium">
              Ver todos →
            </Link>
          </div>
          <div className="divide-y divide-gray-50">
            {stats.clients_by_status.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                No hay tenants aún
              </div>
            ) : (
              stats.clients_by_status.map(row => (
                <div
                  key={row.status}
                  className="flex items-center gap-3 px-5 py-3.5"
                >
                  <ClientStatusBadge status={row.status} />
                  <div className="flex-1" />
                  <span className="text-sm font-semibold text-gray-900">{row.count}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent activity */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Actividad Reciente</h2>
          </div>
          <div className="divide-y divide-gray-50 max-h-96 overflow-auto">
            {recent_activity.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                Sin actividad reciente
              </div>
            ) : (
              recent_activity.map(activity => {
                const style = getActionStyle(activity.action);
                return (
                  <div key={activity.id} className={`px-5 py-3 border-l-3 ${style.border}`}>
                    <div className="flex items-start gap-2.5">
                      <div className={`w-7 h-7 rounded-lg ${style.bg} flex items-center justify-center mt-0.5 shrink-0`}>
                        {style.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">{activity.user_name}</span>{' '}
                          <span className={`${style.labelColor}`}>{getActionLabel(activity.action)}</span>
                        </p>
                        {activity.details && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate">{activity.details}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 shrink-0">
                        {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true, locale: es })}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, suffix, color }: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  suffix?: string;
  color: string;
}) {
  const colorClasses: Record<string, string> = {
    teal: 'bg-teal-50 text-teal-600',
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color] ?? colorClasses.teal}`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">
            {value}{suffix}
          </p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

function ClientStatusBadge({ status }: { status: string }) {
  const variants: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    warm_lead: 'bg-amber-100 text-amber-700',
    cold_lead: 'bg-blue-100 text-blue-700',
    churned: 'bg-rose-100 text-rose-700',
  };

  const labels: Record<string, string> = {
    active: 'Activo',
    warm_lead: 'Lead cálido',
    cold_lead: 'Lead frío',
    churned: 'Baja',
  };

  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${variants[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {labels[status] ?? status}
    </span>
  );
}

function getActionLabel(action: string): string {
  const labels: Record<string, string> = {
    login: 'inició sesión',
    create_client: 'creó un cliente',
    update_client: 'actualizó un cliente',
    delete_client: 'eliminó un cliente',
    create_audit: 'creó una auditoría',
    upload_audit: 'subió resultados de auditoría',
    ingest_audit: 'ingresó auditoría',
    delete_audit: 'eliminó una auditoría',
    create_token: 'generó token de portal',
    generate_proposal: 'generó propuesta',
    create_proposal: 'creó propuesta',
    accept_proposal: 'aceptó propuesta',
    update_proposal: 'actualizó propuesta',
    send_proposal: 'envió propuesta',
  };
  return labels[action] ?? action.replace(/_/g, ' ');
}

function getActionStyle(action: string): { bg: string; border: string; icon: React.ReactNode; labelColor: string } {
  const s = 14;
  const styles: Record<string, { bg: string; border: string; icon: React.ReactNode; labelColor: string }> = {
    login:              { bg: 'bg-slate-100',   border: 'border-slate-300',  icon: <LogIn size={s} className="text-slate-500" />,     labelColor: 'text-slate-500' },
    create_client:      { bg: 'bg-blue-100',    border: 'border-blue-300',   icon: <UserPlus size={s} className="text-blue-600" />,   labelColor: 'text-blue-600' },
    update_client:      { bg: 'bg-blue-50',     border: 'border-blue-200',   icon: <Pencil size={s} className="text-blue-500" />,     labelColor: 'text-blue-500' },
    delete_client:      { bg: 'bg-rose-100',    border: 'border-rose-300',   icon: <Trash2 size={s} className="text-rose-500" />,     labelColor: 'text-rose-500' },
    create_audit:       { bg: 'bg-teal-100',    border: 'border-teal-300',   icon: <FilePlus size={s} className="text-teal-600" />,   labelColor: 'text-teal-600' },
    upload_audit:       { bg: 'bg-teal-100',    border: 'border-teal-300',   icon: <Upload size={s} className="text-teal-600" />,     labelColor: 'text-teal-600' },
    ingest_audit:       { bg: 'bg-teal-100',    border: 'border-teal-300',   icon: <ClipboardCheck size={s} className="text-teal-600" />, labelColor: 'text-teal-600' },
    delete_audit:       { bg: 'bg-rose-100',    border: 'border-rose-300',   icon: <Trash2 size={s} className="text-rose-500" />,     labelColor: 'text-rose-500' },
    create_token:       { bg: 'bg-amber-100',   border: 'border-amber-300',  icon: <Key size={s} className="text-amber-600" />,       labelColor: 'text-amber-600' },
    generate_proposal:  { bg: 'bg-violet-100',  border: 'border-violet-300', icon: <FileText size={s} className="text-violet-600" />, labelColor: 'text-violet-600' },
    create_proposal:    { bg: 'bg-violet-100',  border: 'border-violet-300', icon: <FileText size={s} className="text-violet-600" />, labelColor: 'text-violet-600' },
    send_proposal:      { bg: 'bg-emerald-100', border: 'border-emerald-300',icon: <FileText size={s} className="text-emerald-600" />,labelColor: 'text-emerald-600' },
    accept_proposal:    { bg: 'bg-emerald-100', border: 'border-emerald-300',icon: <FileText size={s} className="text-emerald-600" />,labelColor: 'text-emerald-600' },
    update_proposal:    { bg: 'bg-violet-50',   border: 'border-violet-200', icon: <Pencil size={s} className="text-violet-500" />,   labelColor: 'text-violet-500' },
  };
  return styles[action] ?? { bg: 'bg-gray-100', border: 'border-gray-300', icon: <Clock size={s} className="text-gray-400" />, labelColor: 'text-gray-500' };
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 bg-gray-200 rounded w-48" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 h-20" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 h-80" />
        <div className="bg-white rounded-xl border border-gray-200 h-80" />
      </div>
    </div>
  );
}
